import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import FormLabel from "@ats/src/components/forms/FormLabel";

export interface Option {
  label: string;
  value: string;
}
export type OptionPair = [Option, Option];

interface SlidingToggleSwitchProps {
  name: string;
  value: string; // must match one of the two option values
  options: OptionPair; // exactly two
  onChange: (name: string, value: string) => void;
  label?: string;
  isDisabled?: boolean;
}

const SlidingToggleSwitch: React.FC<SlidingToggleSwitchProps> = ({
  name,
  value,
  options,
  onChange,
  label,
  isDisabled,
}) => {
  const [left, right] = options;
  const rightSelected = value === right.value;

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    onChange(name, e.currentTarget.value);
  };

  const toggle = () => {
    if (isDisabled) return;
    onChange(name, rightSelected ? left.value : right.value);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggle();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (rightSelected) onChange(name, left.value);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (!rightSelected) onChange(name, right.value);
    }
  };

  return (
    <Styled.Root disabled={isDisabled}>
      {label && <FormLabel label={label} />}
      <Styled.RadioRow disabled={isDisabled}>
        <Styled.SideLabel active={!rightSelected} aria-hidden="true">
          {left.label}
        </Styled.SideLabel>

        <Styled.RadioContainer
          aria-label={`${left.label} / ${right.label}`}
          role="radiogroup"
          tabIndex={isDisabled ? -1 : 0}
          onClick={toggle}
          onKeyDown={handleKey}
          disabled={isDisabled}
        >
          <Styled.Radio
            type="radio"
            name={name}
            value={left.value}
            checked={value === left.value}
            onChange={handleRadioChange}
            disabled={isDisabled}
            aria-label={left.label}
          />
          <Styled.Radio
            type="radio"
            name={name}
            value={right.value}
            checked={value === right.value}
            onChange={handleRadioChange}
            disabled={isDisabled}
            aria-label={right.label}
          />
          <Styled.Track disabled={isDisabled}>
            <Styled.Knob pos={rightSelected ? "right" : "left"} disabled={isDisabled} />
          </Styled.Track>
        </Styled.RadioContainer>

        <Styled.SideLabel active={rightSelected} aria-hidden="true">
          {right.label}
        </Styled.SideLabel>
      </Styled.RadioRow>
    </Styled.Root>
  );
};

export default SlidingToggleSwitch;

/* Styled Components
======================================================= */
let Styled: any = {};

Styled.Root = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.6;
    `}
`;

Styled.RadioRow = styled.div((props) => {
  const t: any = props.theme;
  return css`
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[2]};
    ${t.mt(2)}
    ${t.h(8)}
  `;
});

Styled.SideLabel = styled.span<{ active: boolean }>`
  ${(props) => {
    const t: any = props.theme;
    return css`
      ${t.text.sm};
      font-weight: 400;
      user-select: none;
      pointer-events: none;
      font-size: 0.875rem;
      color: ${t.dark ? t.color.gray[200] : t.color.black}; /* inactive */
      ${props.active &&
      css`
        color: ${t.dark ? t.color.gray[200] : t.color.black}; /* active */
      `}
    `;
  }}
`;

Styled.RadioContainer = styled.div<{ disabled?: boolean }>`
  ${(props) => {
    const t: any = props.theme;
    return css`
      position: relative;
      width: 34px;
      height: 20px;
      flex-shrink: 0;
      cursor: pointer;
      outline: none;
      ${props.disabled &&
      css`
        cursor: not-allowed;
        pointer-events: none;
      `}

      > input[type="radio"] {
        position: absolute;
        top: 0;
        height: 100%;
        width: 50%;
        margin: 0;
        opacity: 0.01;
        z-index: 2;
        appearance: none;
        pointer-events: none;
      }
      > input[type="radio"]:first-of-type {
        left: 0;
      }
      > input[type="radio"]:last-of-type {
        left: 50%;
      }

      &:focus-visible > div {
        box-shadow: 0 0 0 2px ${t.dark ? t.color.gray[600] : t.color.gray[600]};
      }
    `;
  }}
`;

Styled.Radio = styled.input``;

Styled.Track = styled.div<{ disabled?: boolean }>`
  ${(props) => {
    const t: any = props.theme;
    return css`
      position: absolute;
      inset: 0;
      border-radius: 999px;
      background: ${t.dark ? t.color.gray[500] : t.color.black};
      pointer-events: none;
      ${!props.disabled &&
      css`
        ${Styled.RadioContainer}:hover & {
          background: ${t.dark ? t.color.gray[400] : t.color.gray[700]};
        }
      `}
      ${props.disabled &&
      css`
        background: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
      `}
    `;
  }}
`;

Styled.Knob = styled.div<{ pos: "left" | "right"; disabled?: boolean }>`
  ${(props) => {
    const t: any = props.theme;
    return css`
      position: absolute;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${t.dark ? t.color.gray[100] : t.color.white};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 1);
      transition: transform 0.25s ease;
      will-change: transform;
      ${props.pos === "right" &&
      css`
        transform: translateX(14px);
      `}
      ${props.disabled &&
      css`
        opacity: 0.7;
        background: ${t.dark ? t.color.gray[400] : t.color.gray[400]};
      `}
    `;
  }}
`;
